import {
  Logo,
  LogoAnswers,
  LogoAthena,
  LogoPayments,
  LogoRewards,
  LogoSet,
} from './index';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export enum Platform {
  EHUB = 'ehub',
  SAVANNA = 'savanna',
  ANSWERS = 'answers',
  REWARDS = 'rewards',
  PAYMENTS = 'payments',
  ATHENA = 'athena',
  FIT = 'fit',
  FIT_STAGING = 'fit-staging',
  FIT_SAVANNA = 'FIT_SAVANNA',
  FIT_STAGING_SAVANNA = 'fit_staging_savanna',
  FIT_PAYMENTS = 'fit_payments',
  FIT_STAGING_PAYMENTS = 'fit_staging_payments',
  SET = 'set',
  SET_STAGING = 'set-staging',
  SET_SAVANNA = 'SET_SAVANNA',
  SET_STAGING_SAVANNA = 'set_staging_savanna',
  SET_PAYMENTS = 'set_payments',
  SET_STAGING_PAYMENTS = 'set_staging_payments',
}

export interface AppInterface {
  name: string;
  displayName: string;
  iconName: string;
  route: string;
  openCheckInModal: boolean;
}
export interface SettingsAppInterface {
  name: string;
  iconName: string;
  route: string;
  followedBySeparator: boolean;
}

export const usePlatforms = (rootDomain: string) => {
  const configApps: AppInterface[] = [
    {
      name: 'eHub',
      displayName: 'eHub',
      iconName: 'HomeIcon',
      route: `https://ehub${rootDomain}`,
      openCheckInModal: false,
    },
    {
      name: 'Learning',
      displayName: 'Learning',
      iconName: 'GraduationCapIcon',
      route: `https://ehub${rootDomain}/learning`,
      openCheckInModal: false,
    },
    {
      name: 'Community',
      displayName: 'Community',
      iconName: 'UsersIcon',
      route: `https://ehub${rootDomain}/community`,
      openCheckInModal: false,
    },
    {
      name: 'Rewards',
      displayName: 'Rewards',
      iconName: 'RewardsIcon',
      route: `https://rewards${rootDomain}`,
      openCheckInModal: false,
    },
    {
      name: 'Check In',
      displayName: 'Check In/Out',
      iconName: 'CheckInIcon',
      route: `/`,
      openCheckInModal: true,
    },
    {
      name: 'Support',
      displayName: 'Support',
      iconName: 'HelpCircleIcon',
      route: `https://ehub${rootDomain}/support`,
      openCheckInModal: false,
    },
    {
      name: 'AnswerBase',
      displayName: 'AnswerBase',
      iconName: 'MessagesSquareIcon',
      route: `https://answers${rootDomain}`,
      openCheckInModal: false,
    },
  ];

  const configSettingsApps: SettingsAppInterface[] = [
    {
      name: 'View profile',
      route: `https://ehub${rootDomain}/profile`,
      iconName: 'UserIcon',
      followedBySeparator: false,
    },
    {
      name: 'Settings',
      route: `https://ehub${rootDomain}/settings`,
      iconName: 'SettingsIcon',
      followedBySeparator: false,
    },
    {
      name: 'Refer a Friend',
      route: `https://rewards${rootDomain}/refer-a-friend`,
      iconName: 'UserPlusIcon',
      followedBySeparator: true,
    },
    {
      name: 'Support',
      route: `https://ehub${rootDomain}/support`,
      iconName: 'HelpCircleIcon',
      followedBySeparator: true,
    },
  ];

  const all = {
    [Platform.EHUB]: {
      id: Platform.EHUB,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://ehub${rootDomain}`,
      apps: configApps,
      settingsApps: configSettingsApps,
      hasThemeSwitcher: true,
      withLogo: false,
      logo: <Logo className="max-h-7 w-full h-auto" />,
      rewardsLink: `https://rewards${rootDomain}`,
      extraCSS: '',
    },
    [Platform.FIT]: {
      id: Platform.FIT,
      homeRoute: `https://fit${rootDomain}/`,
      origin: `https://fit${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://fit${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://pay${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://support.frontiertech.edu/`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://fit${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.FIT_STAGING]: {
      id: Platform.FIT_STAGING,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://ehub${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://ehub${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://payment${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://support.frontiertech.edu/`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://ehub${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.SAVANNA]: {
      id: Platform.SAVANNA,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://savanna${rootDomain}`,
      apps: configApps,
      settingsApps: configSettingsApps,
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <Logo className="max-h-7 w-full h-auto" />,
      rewardsLink: `https://rewards${rootDomain}`,
      extraCSS: '',
    },
    [Platform.FIT_SAVANNA]: {
      id: Platform.FIT_SAVANNA,
      homeRoute: `https://fit${rootDomain}/`,
      origin: `https://learn${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://fit${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://pay${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://support.frontiertech.edu/`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://fit${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.FIT_STAGING_SAVANNA]: {
      id: Platform.FIT_SAVANNA,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://learn${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://ehub${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://payment${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://support.frontiertech.edu/`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://ehub${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.REWARDS]: {
      id: Platform.REWARDS,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://rewards${rootDomain}`,
      apps: configApps,
      settingsApps: configSettingsApps,
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoRewards className="max-h-7 w-full h-auto" />,
      rewardsLink: `https://rewards${rootDomain}`,
      extraCSS: '',
    },
    [Platform.ANSWERS]: {
      id: Platform.ANSWERS,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://answers${rootDomain}`,
      apps: configApps,
      settingsApps: configSettingsApps,
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoAnswers className="max-h-7 w-full h-auto" />,
      rewardsLink: `https://rewards${rootDomain}`,
      extraCSS: '',
    },
    [Platform.PAYMENTS]: {
      id: Platform.PAYMENTS,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://payments${rootDomain}`,
      apps: configApps,
      settingsApps: configSettingsApps,
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoPayments className="max-h-7 w-full h-auto" />,
      rewardsLink: `https://rewards${rootDomain}`,
      extraCSS: '',
    },
    [Platform.FIT_PAYMENTS]: {
      id: Platform.PAYMENTS,
      homeRoute: `https://fit${rootDomain}/`,
      origin: `https://payment${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://fit${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://pay${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://support.frontiertech.edu/`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://fit${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.FIT_STAGING_PAYMENTS]: {
      id: Platform.PAYMENTS,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://payment${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://ehub${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://payment${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://support.frontiertech.edu/`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://ehub${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.ATHENA]: {
      id: Platform.ATHENA,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://athena${rootDomain}`,
      apps: configApps,
      settingsApps: configSettingsApps,
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoAthena className="max-h-7 w-full h-auto" />,
      rewardsLink: `https://rewards${rootDomain}`,
      extraCSS: '',
    },
    [Platform.SET_PAYMENTS]: {
      id: Platform.PAYMENTS,
      homeRoute: `https://set${rootDomain}/`,
      origin: `https://payment${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://set${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://pay${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://help.elmwoodinstitute.org/support/home`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://set${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.SET_STAGING_PAYMENTS]: {
      id: Platform.PAYMENTS,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://payment${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://ehub${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://payment${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://help.elmwoodinstitute.org/support/home`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://ehub${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: true,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.SET_SAVANNA]: {
      id: Platform.SET_SAVANNA,
      homeRoute: `https://set${rootDomain}/`,
      origin: `https://learn${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://set${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://pay${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://help.elmwoodinstitute.org/support/home`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://set${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.SET_STAGING_SAVANNA]: {
      id: Platform.SET_SAVANNA,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://learn${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://ehub${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Learning',
          displayName: 'Learning',
          iconName: 'GraduationCapIcon',
          route: `https://learn${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://payment${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://help.elmwoodinstitute.org/support/home`,
          openCheckInModal: false,
        },
      ],
    },
    [Platform.SET]: {
      id: Platform.SET,
      homeRoute: `https://set${rootDomain}/`,
      origin: `https://set${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://set${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://pay${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://help.elmwoodinstitute.org/support/home`,
          openCheckInModal: false,
        },
      ],
      settingsApps: [
        {
          name: 'Settings',
          route: `https://set${rootDomain}/settings`,
          iconName: 'SettingsIcon',
          followedBySeparator: true,
        },
      ],
      hasThemeSwitcher: false,
      withLogo: false,
      logo: <LogoSet className="max-h-[48px] w-full h-auto" />,
      rewardsLink: undefined,
      extraCSS: 'text-white',
    },
    [Platform.SET_STAGING]: {
      id: Platform.SET_STAGING,
      homeRoute: `https://ehub${rootDomain}/`,
      origin: `https://ehub${rootDomain}`,
      apps: [
        {
          name: 'eHub',
          displayName: 'Home',
          iconName: 'HomeIcon',
          route: `https://ehub${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Community',
          displayName: 'Community',
          iconName: 'UsersIcon',
          route: `https://community${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Payments',
          displayName: 'Payments',
          iconName: 'CreditCardIcon',
          route: `https://payment${rootDomain}`,
          openCheckInModal: false,
        },
        {
          name: 'Support',
          displayName: 'Support',
          iconName: 'HelpCircleIcon',
          route: `https://help.elmwoodinstitute.org/support/home`,
          openCheckInModal: false,
        },
      ],
    },
  };

  let current = undefined;
  const domain = window.location.hostname;
  // savanna LMS
  if (domain.includes('savanna')) {
    current = all[Platform.SAVANNA];
  } else if (domain.includes('learn.set.')) {
    current = all[Platform.FIT_STAGING_SAVANNA];
  } else if (domain.includes('learn.frontiertech.')) {
    current = all[Platform.FIT_SAVANNA];
  } else if (domain.includes('learn.elmwoodinstitute.')) {
    current = all[Platform.SET_SAVANNA];
  } else if (domain.includes('learn') && domain.includes('.fit')) {
    // New .fit domains: keep FIT branding, existing (old) URLs until the
    // redirect PR ships.
    current = all[Platform.FIT_SAVANNA];
  }
  // eHub
  else if (domain.includes('ehub.set.')) {
    current = all[Platform.FIT_STAGING];
  } else if (domain.includes('ehub.frontiertech.')) {
    current = all[Platform.FIT];
  } else if (domain.includes('set.elmwoodinstitute.')) {
    current = all[Platform.SET];
  } else if (domain.includes('ehub') && domain.includes('.fit')) {
    // New .fit domains: keep FIT branding, existing (old) URLs until the
    // redirect PR ships.
    current = all[Platform.FIT];
  } else if (domain.includes('ehub')) {
    current = all[Platform.EHUB];
  }
  // Payments
  else if (domain.includes('payment.set')) {
    current = all[Platform.FIT_STAGING_PAYMENTS];
  } else if (domain.includes('pay.elmwoodinstitute.')) {
    current = all[Platform.SET_PAYMENTS];
  } else if (domain.includes('pay.frontiertech.')) {
    current = all[Platform.FIT_PAYMENTS];
  } else if (domain.includes('pay') && domain.includes('.fit')) {
    // New .fit domains: keep FIT branding, existing (old) URLs until the
    // redirect PR ships.
    current = all[Platform.FIT_PAYMENTS];
  } else if (domain.includes('payment')) {
    current = all[Platform.PAYMENTS];
  }
  // Other
  else if (domain.includes('rewards')) {
    current = all[Platform.REWARDS];
  } else if (domain.includes('answers')) {
    current = all[Platform.ANSWERS];
  } else if (domain.includes('athena')) {
    current = all[Platform.ATHENA];
  } else {
    current = all[Platform.FIT_STAGING];
  }

  if (!current || !domain) {
    throw new Error(`Unsupported host.`);
  }

  return { current, all: all as Record<Platform, typeof current> };
};

export const ExternalApplicationLinks = {
  ehub: `https://ehub${process.env['NX_COOKIE_DOMAIN']}/`,
  rewards: `https://rewards${process.env['NX_COOKIE_DOMAIN']}/`,
  admissions: `https://admissions${process.env['NX_COOKIE_DOMAIN']}/`,
  payments: `https://payment${process.env['NX_COOKIE_DOMAIN']}/`,
  community: `https://community${process.env['NX_COOKIE_DOMAIN']}/oauth2/initiate`,
  support: process.env['NX_SUPPORT_URL'],
  answerBase: `https://answers${process.env['NX_COOKIE_DOMAIN']}/`,
  set: `${process.env['NX_SET_URL'] ?? '/'}`,
};

export const useScrollRestoration = () => {
  // Depend on pathname so same-component navigations (e.g. /program-preview/A
  // → /program-preview/B via a related-program link) also scroll to top.
  // With an empty dep array React Router's component reuse would skip this.
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.scrollY > 0) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
};

const welcomeMessages = [
  (name: string) => ({
    title: `Hello ${name}!`,
    message: `Today is your day to shine. Let's make it happen!`,
  }),
  (name: string) => ({
    title: `Hello ${name}!`,
    message: `Your dreams are worth pursuing. We're here to help you reach them.`,
  }),
  (name: string) => ({
    title: `Welcome back, ${name}! `,
    message: `Every step you take brings you closer to success.`,
  }),
  (name: string) => ({
    title: `Hello ${name}!`,
    message: `Believe in yourself. You have the power to change the world.`,
  }),
  (name: string) => ({
    title: `Hello ${name}!`,
    message: `The future is yours to create. Let's get started!`,
  }),
  (name: string) => ({
    title: `Welcome, ${name}!`,
    message: `Your learning journey begins today. Let's explore together!`,
  }),
  (name: string) => ({
    title: `Welcome, ${name}!`,
    message: `Your learning journey begins today. Let's explore together!`,
  }),
  (name: string) => ({
    title: `Hello ${name}!`,
    message: `Curiosity is the key to unlocking new knowledge. Keep asking questions!`,
  }),
  (name: string) => ({
    title: `Hello ${name}!`,
    message: `Embrace challenges. They're opportunities for growth.`,
  }),
  (name: string) => ({
    title: `Welcome back, ${name}!`,
    message: `Let's turn those lessons into action.`,
  }),
  (name: string) => ({
    title: `Welcome back, ${name}!`,
    message: `Learning is a lifelong adventure. Enjoy the ride!`,
  }),
  (name: string) => ({
    title: `Hello ${name}`,
    message: `We're so glad you're here!`,
  }),
  (name: string) => ({
    title: `Hello ${name}`,
    message: `Your dedication inspires us. Keep going!`,
  }),
  (name: string) => ({
    title: `Welcome back, ${name}`,
    message: `You've got this!`,
  }),
  (name: string) => ({
    title: `Welcome back, ${name}`,
    message: `Your potential is limitless. Let's unlock it together.`,
  }),
  (name: string) => ({
    title: `Hello ${name}`,
    message: `Let's make today a great one!`,
  }),
];

export function getDayMessage(name: string): {
  title: string;
  message: string;
} {
  const date = new Date();
  const startOfYear = new Date(date.getFullYear(), 0, 1); // January 1st of the given year
  const diffInMs = date.getTime() - startOfYear.getTime(); // Difference in milliseconds
  const dayInMs = 24 * 60 * 60 * 1000; // Number of milliseconds in one day

  const dayOfYear = Math.floor(diffInMs / dayInMs) + 1;
  const welcomeMsgIndex = dayOfYear % (welcomeMessages.length - 1);
  return welcomeMessages[welcomeMsgIndex](name);
}

export const downloadFromUrl = (filename: string, url?: string | null) => {
  if (!url) {
    return;
  }
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const link = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      console.error('Error downloading the file:', error);
    });
};

export const useIsSet = () => {
  const { current } = usePlatforms(process.env['NX_COOKIE_DOMAIN'] || '');
  return [
    Platform.FIT,
    Platform.FIT_STAGING,
    Platform.FIT_SAVANNA,
    Platform.FIT_STAGING_SAVANNA,
    Platform.FIT_PAYMENTS,
    Platform.FIT_STAGING_PAYMENTS,
  ].includes(current.id as Platform);
};
